import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import {
  getNotificationList,
  markAllNotificationsRead,
  markNotificationAsRead,
} from '@/api/notifications';
import { FROM_NOTIFICATION_PARAM } from '@/constants/queryParams';
import { IS_ADMIN_KEY } from '@/constants/roles';
import { useApiInfiniteQuery } from '@/hooks/api';
import { LISTING_STALE_TIME } from '@/hooks/api/defaults';
import { useUnreadNotificationsCount } from '@/hooks/queries/useUnreadCounts';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLoadMoreOnScroll } from '@/hooks/useLoadMoreOnScroll';
import useLocale from '@/hooks/useLocale';
import { useUrlState } from '@/hooks/useUrlState';
import { queryKeys } from '@/shared/lib/queryKeys';
import {
  parseCursorListingResponse,
  type CursorListingApiPayload,
  type ParsedCursorListingResult,
} from '@/UI/filteredListing';
import { getLocalStorageItem } from '@/utils/localStorage';

import type { NotificationProps } from '@/features/notifications/resolveNotification';

type UseNotificationPopupDataOptions = {
  onClose?: () => void;
};

type NotificationListPage = ParsedCursorListingResult<NotificationProps>;

export function useNotificationPopupData({
  onClose,
}: UseNotificationPopupDataOptions = {}) {
  const { history } = useUrlState();
  const queryClient = useQueryClient();
  const { sessionLocale: locale } = useLocale();
  const isAdmin = getLocalStorageItem(IS_ADMIN_KEY);
  const { data: unreadCount = 0 } = useUnreadNotificationsCount({
    enabled: !isAdmin,
  });
  const { isClient, isRecruiter } = useCurrentUser();

  const adjustUnreadCount = useCallback(
    (delta: number) => {
      queryClient.setQueryData<number>(
        queryKeys.notifications.unread(),
        (current) => Math.max(0, (current ?? 0) + delta),
      );
    },
    [queryClient],
  );

  const resetUnreadCount = useCallback(() => {
    queryClient.setQueryData(queryKeys.notifications.unread(), 0);
  }, [queryClient]);

  const markListItemsRead = useCallback(
    (ids?: ReadonlySet<string>) => {
      const markRow = (row: NotificationProps): NotificationProps =>
        !ids || ids.has(row.id) ? { ...row, read: true } : row;

      queryClient.setQueryData<InfiniteData<NotificationListPage>>(
        queryKeys.notifications.list(),
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  rows: page.rows.map(markRow),
                })),
              }
            : current,
      );
    },
    [queryClient],
  );

  const query = useApiInfiniteQuery<
    NotificationListPage,
    Error,
    InfiniteData<NotificationListPage>,
    readonly unknown[],
    string | undefined
  >({
    queryKey: queryKeys.notifications.list(),
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const res = await getNotificationList(
        pageParam ? `cursor=${encodeURIComponent(pageParam)}` : undefined,
      );
      return parseCursorListingResponse<NotificationProps>(
        res as unknown as CursorListingApiPayload<NotificationProps>,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: LISTING_STALE_TIME,
  });

  const notifications = useMemo(
    () => query.data?.pages.flatMap((page) => page.rows) ?? [],
    [query.data?.pages],
  );

  const markAsRead = useCallback(() => {
    void markAllNotificationsRead().then(() => {
      resetUnreadCount();
      markListItemsRead();
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.unread(),
      });
    });
  }, [markListItemsRead, queryClient, resetUnreadCount]);

  const loadMoreNotifications = useCallback(() => {
    if (!query.hasNextPage || query.isFetchingNextPage) {
      return Promise.resolve();
    }
    return query.fetchNextPage().then(() => undefined);
  }, [query.fetchNextPage, query.hasNextPage, query.isFetchingNextPage]);

  const visibleNotifications = notifications.filter(
    (nt) => nt.notification_type !== 1 && nt.notification_type !== 16,
  );

  const { scrollRef, onScroll } = useLoadMoreOnScroll({
    hasMore: !!query.hasNextPage,
    isLoading: query.isLoading || query.isFetchingNextPage,
    onLoadMore: loadMoreNotifications,
    itemCount: visibleNotifications.length,
    autoLoadOnMount: true,
  });

  const handleNotificationClick = useCallback(
    (
      link: string,
      disabled: boolean,
      id: string,
      closePopover?: () => void,
    ) => {
      if (disabled || !link) return;
      void markNotificationAsRead(id).then(() => {
        const separator = link.includes('?') ? '&' : '?';
        history.push(`${link}${separator}${FROM_NOTIFICATION_PARAM}=1`);
        (closePopover ?? onClose)?.();
        adjustUnreadCount(-1);
        markListItemsRead(new Set([id]));
      });
    },
    [adjustUnreadCount, history, markListItemsRead, onClose],
  );

  return {
    isLoading: query.isLoading,
    unreadCount,
    isAdmin,
    isClient,
    isRecruiter,
    locale,
    visibleNotifications,
    scrollRef,
    onScroll,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage ?? false,
    markAsRead,
    handleNotificationClick,
  };
}
